import axios from 'axios';

const AUTH0_DOMAIN = process.env.REACT_APP_AUTH0_DOMAIN || 'dev-8d807quvd3sioyuc.us.auth0.com';
const AUTH0_CLIENT_ID = process.env.REACT_APP_AUTH0_CLIENT_ID || 'dm8ZnXY5o4gggCIn8pEodTtG0xmEJSVd';
const AUTH0_AUDIENCE = process.env.REACT_APP_AUTH0_AUDIENCE || 'https://hockeycoach.com';

export const auth0Service = {
  // Login using Auth0's database connection
  async loginWithPassword(email, password) {
    try {
      // Use password grant with Auth0's database connection
      const response = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
        grant_type: 'password',
        username: email,
        password: password,
        client_id: AUTH0_CLIENT_ID,
        audience: AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access read:current_user update:current_user_metadata'
      });

      // Store tokens
      localStorage.setItem('biscuit_access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('id_token', response.data.id_token);

      // Get user info
      const userInfo = await this.getUserInfo(response.data.access_token);
      localStorage.setItem('user', JSON.stringify(userInfo));

      return userInfo;
    } catch (error) {
      console.error('Auth0 login error:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.data?.error === 'invalid_grant') {
        throw new Error('Invalid email or password');
      }
      if (error.response?.data?.error === 'access_denied') {
        throw new Error('Access denied. Please check your Auth0 configuration.');
      }
      if (error.response?.data?.error_description) {
        throw new Error(error.response.data.error_description);
      }
      throw new Error(`Login failed: ${error.response?.data?.error || error.message}`);
    }
  },

  // Signup with username/password
  async signup(email, password, firstName, lastName) {
    try {
      // First, create the user in Auth0's database
      const signupResponse = await axios.post(`https://${AUTH0_DOMAIN}/dbconnections/signup`, {
        client_id: AUTH0_CLIENT_ID,
        email: email,
        password: password,
        name: `${firstName} ${lastName}`,
        connection: 'Username-Password-Authentication'
      });

      // Then login with the new credentials
      return await this.loginWithPassword(email, password);
    } catch (error) {
      console.error('Auth0 signup error:', error);
      console.error('Error response:', error.response?.data);
      
      if (error.response?.data?.error === 'user_exists') {
        throw new Error('User already exists with this email');
      }
      if (error.response?.data?.error_description) {
        throw new Error(error.response.data.error_description);
      }
      throw new Error(`Signup failed: ${error.response?.data?.error || error.message}`);
    }
  },

  // Handle the callback from Auth0
  async handleCallback(code) {
    try {
      // Exchange code for tokens
      const response = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
        grant_type: 'authorization_code',
        client_id: AUTH0_CLIENT_ID,
        audience: AUTH0_AUDIENCE,
        code: code,
        redirect_uri: `${window.location.origin}/callback`
      });

      // Store tokens
      localStorage.setItem('biscuit_access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('id_token', response.data.id_token);

      // Get user info
      const userInfo = await this.getUserInfo(response.data.access_token);
      localStorage.setItem('user', JSON.stringify(userInfo));

      return userInfo;
    } catch (error) {
      console.error('Auth0 callback error:', error);
      throw new Error('Authentication failed. Please try again.');
    }
  },

  // Get user info from Auth0
  async getUserInfo(accessToken) {
    try {
      const response = await axios.get(`https://${AUTH0_DOMAIN}/userinfo`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error('Error getting user info:', error);
      throw error;
    }
  },

  // Refresh token
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem('refresh_token');
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
        grant_type: 'refresh_token',
        client_id: AUTH0_CLIENT_ID,
        audience: AUTH0_AUDIENCE,
        refresh_token: refreshToken
      });

      localStorage.setItem('biscuit_access_token', response.data.access_token);
      localStorage.setItem('refresh_token', response.data.refresh_token);
      localStorage.setItem('id_token', response.data.id_token);

      return response.data.access_token;
    } catch (error) {
      console.error('Error refreshing token:', error);
      this.logout();
      throw error;
    }
  },

  // Logout
  logout() {
    localStorage.removeItem('biscuit_access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('id_token');
    localStorage.removeItem('user');
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('biscuit_access_token');
  },

  // Get current user
  getCurrentUser() {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },

  // Get access token
  getAccessToken() {
    return localStorage.getItem('biscuit_access_token');
  },

  // Create axios instance with auth headers
  createAuthenticatedApi() {
    const api = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'https://www.flyoverfabric.com/api',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add request interceptor to include auth token
    api.interceptors.request.use(
      (config) => {
        const token = this.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Add response interceptor to handle auth errors
    api.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          const errorData = error.response.data;
          // Check if it's a JWT expiration error
          if (errorData?.error === 'jwt expired' || errorData?.message?.includes('jwt expired')) {
            console.log('JWT expired in auth0Service, redirecting to login');
            this.logout();
            window.location.href = '/login';
            return;
          }
          
          try {
            // Try to refresh the token for other 401 errors
            await this.refreshToken();
            // Retry the original request
            const token = this.getAccessToken();
            error.config.headers.Authorization = `Bearer ${token}`;
            return axios.request(error.config);
          } catch (refreshError) {
            // If refresh fails, logout and redirect
            this.logout();
            window.location.href = '/login';
          }
        }
        return Promise.reject(error);
      }
    );

    return api;
  },

  // Test Auth0 configuration
  async testAuth0Config() {
    try {
      console.log('Testing Auth0 configuration...');
      console.log('Domain:', AUTH0_DOMAIN);
      console.log('Client ID:', AUTH0_CLIENT_ID);
      console.log('Audience:', AUTH0_AUDIENCE);
      
      // Test the userinfo endpoint to verify domain
      const response = await axios.get(`https://${AUTH0_DOMAIN}/.well-known/openid_configuration`);
      
      console.log('Auth0 domain is accessible');
      console.log('OpenID Configuration:', response.data);
      return true;
    } catch (error) {
      console.error('Auth0 configuration test failed:', error.response?.data || error.message);
      return false;
    }
  },

  // Create a test user in Auth0 (for development only)
  async createTestUser(email, password, firstName, lastName) {
    try {
      console.log('Creating test user in Auth0...');
      
      const response = await axios.post(`https://${AUTH0_DOMAIN}/dbconnections/signup`, {
        client_id: AUTH0_CLIENT_ID,
        email: email,
        password: password,
        name: `${firstName} ${lastName}`,
        connection: 'Username-Password-Authentication'
      });
      
      console.log('Test user created successfully!');
      return response.data;
    } catch (error) {
      console.error('Failed to create test user:', error.response?.data || error.message);
      throw error;
    }
  },

  // Test if a user exists (this will fail if user doesn't exist, which is expected)
  async testUserExists(email, password) {
    try {
      console.log('Testing if user exists...');
      
      const response = await axios.post(`https://${AUTH0_DOMAIN}/oauth/token`, {
        grant_type: 'password',
        username: email,
        password: password,
        client_id: AUTH0_CLIENT_ID,
        audience: AUTH0_AUDIENCE,
        scope: 'openid profile email offline_access read:current_user update:current_user_metadata'
      });
      
      console.log('User exists and credentials are valid!');
      return true;
    } catch (error) {
      if (error.response?.data?.error === 'invalid_grant') {
        console.log('User does not exist or credentials are invalid');
        return false;
      }
      console.error('Error testing user:', error.response?.data || error.message);
      return false;
    }
  }
};

export default auth0Service; 